import { useCallback } from 'react';
import { MapLocationData, LocationChangeHandler } from '@hero/web-components';
import { LatLng } from '@hero/schema';

export interface UseSingleLocationEditOptions {
  /** Current editing index from useMapLocations */
  editingIndex: number | null;
  /** Current locations array */
  locations: MapLocationData[];
  /** Callback when location changes */
  onLocationChange?: LocationChangeHandler;
  /** Map instance */
  map: google.maps.Map | null;
}

export function useSingleLocationEdit(options: UseSingleLocationEditOptions) {
  const { editingIndex, locations, onLocationChange, map } = options;

  // Determine if we're in single location edit mode
  const isSingleLocationEditMode = editingIndex !== null;

  // Get the location being edited
  const editingLocation = editingIndex !== null ? locations[editingIndex] : null;

  // Determine if a marker should be hidden (when editing a different location)
  const shouldHideMarker = useCallback(
    (index: number) => {
      return isSingleLocationEditMode && editingIndex !== index;
    },
    [isSingleLocationEditMode, editingIndex],
  );

  // Handle map click in edit mode - just update coordinates immediately
  // Address fetching will be handled by MapSearch component via ref
  const handleMapClickInEditMode = useCallback(
    async (
      event: google.maps.MapMouseEvent,
      onCoordinatesSelect?: (lat: number, lng: number, index: number) => Promise<void>,
    ) => {
      if (
        !isSingleLocationEditMode ||
        !event.latLng ||
        !editingLocation ||
        !onLocationChange ||
        editingIndex === null
      ) {
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const cords: LatLng = { lat, lng };

      // Update location coordinates immediately (marker moves instantly)
      // When clicking map, mark as potentially inaccurate (unless it's a saved location)
      const updatedLocationImmediate: MapLocationData = {
        ...editingLocation,
        cords,
        isLoadingAddress: true,
        isAccurateLocation: false, // Any coordinate change during editing = inaccurate until confirmed
      };

      // Update the location immediately so marker moves
      onLocationChange(updatedLocationImmediate, editingIndex);

      // Pan to clicked location; zoom to min level only if currently more zoomed out
      if (map) {
        map.panTo({ lat, lng });
        const currentZoom = map.getZoom();
        if (typeof currentZoom === 'number' && currentZoom < 15) {
          map.setZoom(15);
        }
      }

      // Let MapSearch component handle address fetching
      if (onCoordinatesSelect) {
        await onCoordinatesSelect(lat, lng, editingIndex);
      }
    },
    [isSingleLocationEditMode, editingLocation, editingIndex, onLocationChange, map],
  );

  return {
    isSingleLocationEditMode,
    editingLocation,
    shouldHideMarker,
    handleMapClickInEditMode,
  };
}
